import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent } from "vue"

import { computed, onMounted, ref, watch } from "vue";
import { BasicCommentSchema, CommentSchema } from "@/types";
import { timestampToDisplay } from "@/util";
import {
  artifactStore,
  commentApiStore,
  commentStore,
  permissionStore,
  selectionStore,
} from "@/hooks";
import {
  PanelCard,
  ListItem,
  FlexBox,
  Icon,
  Typography,
  IconButton,
  TextButton,
  PopupEditInput,
} from "@/components/common";


/**
 * Displays artifact comments, flags, and health checks.
 */
const __default__ = {
  name: "ArtifactComments",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const showResolved = ref(false);
const newComment = ref("");
const commentType = ref<"conversation" | "flag">("conversation");
const editedComment = ref<BasicCommentSchema | null>(null);

const artifactId = computed(() => selectionStore.selectedArtifactId);
const artifact = computed(() => artifactStore.selectedArtifact);

const allComments = computed(() =>
  commentStore.getCommentsAndFlags(artifactId.value, showResolved.value)
);

/**
 * Resets the comment input fields.
 */
function handleReset() {
  newComment.value = "";
  commentType.value = "conversation";
  editedComment.value = null;
}

/**
 * Adds a new comment to the artifact.
 */
function handleAddComment() {
  if (!artifact.value) return;

  commentApiStore.handleAddComment(
    artifact.value,
    newComment.value,
    commentType.value,
    {
      onSuccess: handleReset,
    }
  );
}

/**
 * Resolves a comment, hiding it from the list.
 * @param comment - The comment to resolve.
 */
function handleResolveComment(comment: BasicCommentSchema) {
  if (!artifact.value) return;

  commentApiStore.handleResolveComment(artifact.value, comment);
}

/**
 * Enables a comments edit mode,
 * or disables comment editing if already enabled on the given comment.
 * @param comment - The comment to edit.
 */
function handleEditComment(comment: BasicCommentSchema) {
  if (!editedComment.value || editedComment.value.id !== comment.id) {
    editedComment.value = comment;
  } else {
    editedComment.value = null;
  }
}

/**
 * Closes the edited comment.
 */
function handleCloseEditedComment() {
  editedComment.value = null;
}

/**
 * Saves the edited comment.
 */
function handleSaveEditedComment(content: string) {
  if (!editedComment.value || !artifact.value) return;

  editedComment.value.content = content;

  commentApiStore.handleEditComment(artifact.value, editedComment.value, {
    onSuccess: handleReset,
  });
}

/**
 * Deletes a comment.
 * @param comment - The comment to delete.
 */
function handleDeleteComment(comment: CommentSchema) {
  if (!artifact.value) return;

  commentApiStore.handleDeleteComment(artifact.value, comment.id);
}

onMounted(() => {
  commentApiStore.handleLoadComments(artifactId.value);
});

watch(
  () => artifactId.value,
  () => {
    handleReset();
    commentApiStore.handleLoadComments(artifactId.value);
  }
);

return (_ctx: any,_cache: any) => {
  const _component_q_btn_dropdown = _resolveComponent("q-btn-dropdown")!
  const _component_q_input = _resolveComponent("q-input")!

  return (_unref(permissionStore).isNASA)
    ? (_openBlock(), _createBlock(_unref(PanelCard), {
        key: 0,
        borderless: "",
        collapsable: "",
        title: "Feedback"
      }, {
        "title-actions": _withCtx(() => [
          _createVNode(_unref(TextButton), {
            label: showResolved.value ? 'Hide Resolved' : 'Show Resolved',
            icon: "comment-resolve",
            text: "",
            small: "",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showResolved.value = !showResolved.value))
          }, null, 8, ["label"])
        ]),
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allComments.value, (comment) => {
            return (_openBlock(), _createBlock(_unref(ListItem), {
              key: comment.id,
              dense: "",
              class: _normalizeClass(
        comment.type === 'flag'
          ? 'artifact-comment artifact-flag'
          : 'artifact-comment'
      ),
              "action-cols": 3
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(PopupEditInput), {
                  value: editedComment.value ? editedComment.value.content : '',
                  multiline: "",
                  "full-width": "",
                  editing: editedComment.value && editedComment.value.id === comment.id,
                  onOpen: ($event: any) => (handleEditComment(comment)),
                  onClose: handleCloseEditedComment,
                  onSave: handleSaveEditedComment
                }, {
                  icon: _withCtx(() => [
                    _createVNode(_unref(Icon), {
                      variant: comment.type === 'flag' ? 'flag' : 'account',
                      size: "sm",
                      class: "q-mr-md",
                      color: comment.type === 'flag' ? 'secondary' : undefined
                    }, null, 8, ["variant", "color"])
                  ]),
                  actions: _withCtx(() => [
                    (comment.status !== 'resolved')
                      ? (_openBlock(), _createBlock(_unref(IconButton), {
                          key: 0,
                          small: "",
                          icon: "comment-resolve",
                          color: "text",
                          tooltip: "Resolve",
                          onClick: ($event: any) => (handleResolveComment(comment))
                        }, null, 8, ["onClick"]))
                      : _createCommentVNode("", true),
                    (comment.status !== 'resolved')
                      ? (_openBlock(), _createBlock(_unref(IconButton), {
                          key: 1,
                          small: "",
                          icon: "delete",
                          tooltip: "Delete",
                          onClick: ($event: any) => (handleDeleteComment(comment))
                        }, null, 8, ["onClick"]))
                      : _createCommentVNode("", true),
                    (comment.status === 'resolved')
                      ? (_openBlock(), _createBlock(_unref(IconButton), {
                          key: 2,
                          small: "",
                          icon: "comment",
                          color: "text",
                          class: "show-on-hover-child",
                          tooltip: "Unresolve",
                          onClick: ($event: any) => (handleResolveComment(comment))
                        }, null, 8, ["onClick"]))
                      : _createCommentVNode("", true),
                    (comment.status === 'resolved')
                      ? (_openBlock(), _createBlock(_unref(Typography), {
                          key: 3,
                          secondary: "",
                          small: "",
                          value: "Resolved",
                          l: "1"
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  body: _withCtx(() => [
                    _createVNode(_unref(Typography), {
                      value: comment.content,
                      el: "p"
                    }, null, 8, ["value"])
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_unref(Typography), {
                      value: comment.userId
                    }, null, 8, ["value"]),
                    _createVNode(_unref(Typography), {
                      secondary: "",
                      small: "",
                      value: _unref(timestampToDisplay)(comment.updatedAt),
                      l: "2",
                      ellipsis: ""
                    }, null, 8, ["value"])
                  ]),
                  _: 2
                }, 1032, ["value", "editing", "onOpen"])
              ]),
              _: 2
            }, 1032, ["class"]))
          }), 128)),
          _createVNode(_component_q_input, {
            modelValue: newComment.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newComment).value = $event)),
            autogrow: "",
            type: "textarea",
            placeholder: "Add a comment...",
            class: "q-ml-md bd-transparent"
          }, {
            before: _withCtx(() => [
              _createVNode(_unref(Icon), {
                variant: "account",
                size: "sm"
              })
            ]),
            append: _withCtx(() => [
              _createVNode(_component_q_btn_dropdown, {
                flat: "",
                "auto-close": "",
                dense: "",
                size: "sm",
                icon: commentType.value === 'conversation' ? 'comment' : 'flag'
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(FlexBox), { column: "" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(TextButton), {
                        label: "Comment",
                        icon: "comment",
                        small: "",
                        text: "",
                        block: "",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (commentType.value = 'conversation'))
                      }),
                      _createVNode(_unref(TextButton), {
                        label: "Flag",
                        icon: "flag",
                        small: "",
                        block: "",
                        text: "",
                        align: "start",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (commentType.value = 'flag'))
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["icon"]),
              _createVNode(_unref(IconButton), {
                small: "",
                disabled: !newComment.value,
                tooltip: "Add comment",
                icon: "forward",
                onClick: handleAddComment
              }, null, 8, ["disabled"])
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})